import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FeatureRoute } from './components/FeatureRoute';
import { AdminRoute, ProtectedRoute } from './components/ProtectedRoute';
import { CustomerDetailPage } from './features/customers/CustomerDetailPage';
import { CustomerFormPage } from './features/customers/CustomerFormPage';
import { CustomerListPage } from './features/customers/CustomerListPage';
import { InvoiceDetailPage } from './features/invoices/InvoiceDetailPage';
import { InvoiceFormPage } from './features/invoices/InvoiceFormPage';
import { InvoiceListPage } from './features/invoices/InvoiceListPage';
import { CompanyExpenseFormPage } from './features/company-expenses/CompanyExpenseFormPage';
import { CompanyExpenseListPage } from './features/company-expenses/CompanyExpenseListPage';
import { CompanyExpensePayablesPage } from './features/company-expenses/CompanyExpensePayablesPage';
import { CompanyExpenseSummaryPage } from './features/company-expenses/CompanyExpenseSummaryPage';
import { MydataConnectionPage } from './features/mydata/MydataConnectionPage';
import { MydataDebtsPage } from './features/mydata/MydataDebtsPage';
import { MydataDocumentPage } from './features/mydata/MydataDocumentPage';
import { MydataDocumentsListPage } from './features/mydata/MydataDocumentsListPage';
import { MydataPdfExportPage } from './features/mydata/MydataPdfExportPage';
import { MydataPeriodPage } from './features/mydata/MydataPeriodPage';
import { MydataSyncPage } from './features/mydata/MydataSyncPage';
import { PaymentDetailPage } from './features/payments/PaymentDetailPage';
import { PaymentFormPage } from './features/payments/PaymentFormPage';
import { PaymentListPage } from './features/payments/PaymentListPage';
import { CompanySetupPage } from './features/platform/CompanySetupPage';
import { PlatformAdminsPage } from './features/platform/PlatformAdminsPage';
import { AppPackagesPage } from './features/platform/AppPackagesPage';
import { AuditLogsPage } from './features/platform/AuditLogsPage';
import { PlatformSettingsPage } from './features/platform/PlatformSettingsPage';
import { TenantsPage } from './features/platform/TenantsPage';
import { AgingPage } from './features/reports/AgingPage';
import { AgreementVariancePage } from './features/reports/AgreementVariancePage';
import { CategorySummaryPage } from './features/reports/CategorySummaryPage';
import { CompanyExpenseAgingPage } from './features/reports/CompanyExpenseAgingPage';
import { CompanyExpenseByVendorPage } from './features/reports/CompanyExpenseByVendorPage';
import { CompanyExpensePaymentsPage } from './features/reports/CompanyExpensePaymentsPage';
import { CompanyExpenseTypeAnalysisPage } from './features/reports/CompanyExpenseTypeAnalysisPage';
import { CompanyExpenseYearlyMonthlyPage } from './features/reports/CompanyExpenseYearlyMonthlyPage';
import { CustomerBalancesPage } from './features/reports/CustomerBalancesPage';
import { MonthlySummaryPage } from './features/reports/MonthlySummaryPage';
import { ShipmentByCustomerPage } from './features/reports/ShipmentByCustomerPage';
import { ShipmentByPortVesselPage } from './features/reports/ShipmentByPortVesselPage';
import { ShipmentContainerBlPage } from './features/reports/ShipmentContainerBlPage';
import { ShipmentExpenseAnalysisPage } from './features/reports/ShipmentExpenseAnalysisPage';
import { ShipmentExpenseCategoriesPage } from './features/reports/ShipmentExpenseCategoriesPage';
import { ShipmentMonthlySummaryPage } from './features/reports/ShipmentMonthlySummaryPage';
import { ShipmentOpenFilesPage } from './features/reports/ShipmentOpenFilesPage';
import { UnallocatedPaymentsPage } from './features/reports/UnallocatedPaymentsPage';
import { YearlyMonthlyPage } from './features/reports/YearlyMonthlyPage';
import { InvoiceCategoriesPage } from './features/settings/InvoiceCategoriesPage';
import { ExpenseCategoriesPage } from './features/settings/ExpenseCategoriesPage';
import { CompanyExpenseCategoriesPage } from './features/settings/CompanyExpenseCategoriesPage';
import { OfficesPage } from './features/settings/OfficesPage';
import { UsersPage } from './features/settings/UsersPage';
import { ShipmentFileDetailPage } from './features/shipment-files/ShipmentFileDetailPage';
import { ShipmentFileFormPage } from './features/shipment-files/ShipmentFileFormPage';
import { ShipmentFileListPage } from './features/shipment-files/ShipmentFileListPage';
import { ConfirmProvider } from './components/ui';
import { SettingsProvider } from './contexts/SettingsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './hooks/useAuth';
import { AppLayout } from './layouts/AppLayout';
import { PlatformLayout } from './layouts/PlatformLayout';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';

export default function App() {
  return (
    <ThemeProvider>
    <SettingsProvider>
    <AuthProvider>
    <ConfirmProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/reset-password/:token"
            element={<ResetPasswordPage />}
          />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="setup/company" element={<CompanySetupPage />} />

            <Route path="platform" element={<PlatformLayout />}>
              <Route index element={<Navigate to="tenants" replace />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="packages" element={<AppPackagesPage />} />
              <Route path="admins" element={<PlatformAdminsPage />} />
              <Route path="audit-logs" element={<AuditLogsPage />} />
              <Route path="settings" element={<PlatformSettingsPage />} />
            </Route>

            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />

              <Route element={<FeatureRoute feature="customers" />}>
                <Route path="customers" element={<CustomerListPage />} />
                <Route path="customers/new" element={<CustomerFormPage />} />
                <Route path="customers/:id" element={<CustomerDetailPage />} />
                <Route
                  path="customers/:id/edit"
                  element={<CustomerFormPage />}
                />
              </Route>

              <Route element={<FeatureRoute feature="shipment_files" />}>
                <Route
                  path="shipment-files"
                  element={<ShipmentFileListPage />}
                />
                <Route
                  path="shipment-files/new"
                  element={<ShipmentFileFormPage />}
                />
                <Route
                  path="shipment-files/:id"
                  element={<ShipmentFileDetailPage />}
                />
                <Route
                  path="shipment-files/:id/edit"
                  element={<ShipmentFileFormPage />}
                />
              </Route>

              <Route element={<FeatureRoute feature="invoices" />}>
                <Route path="invoices" element={<InvoiceListPage />} />
                <Route path="invoices/new" element={<InvoiceFormPage />} />
                <Route path="invoices/:id" element={<InvoiceDetailPage />} />
                <Route path="invoices/:id/edit" element={<InvoiceFormPage />} />
              </Route>

              <Route element={<FeatureRoute feature="payments" />}>
                <Route path="payments" element={<PaymentListPage />} />
                <Route path="payments/new" element={<PaymentFormPage />} />
                <Route path="payments/:id" element={<PaymentDetailPage />} />
                <Route path="payments/:id/edit" element={<PaymentFormPage />} />
              </Route>

              <Route element={<FeatureRoute feature="company_expenses" />}>
                <Route
                  path="company-expenses"
                  element={<CompanyExpenseListPage />}
                />
                <Route
                  path="company-expenses/new"
                  element={<CompanyExpenseFormPage />}
                />
                <Route
                  path="company-expenses/payables"
                  element={<CompanyExpensePayablesPage />}
                />
                <Route
                  path="company-expenses/summary"
                  element={<CompanyExpenseSummaryPage />}
                />
                <Route
                  path="company-expenses/:id/edit"
                  element={<CompanyExpenseFormPage />}
                />
              </Route>

              <Route element={<FeatureRoute feature="mydata" />}>
                <Route path="mydata" element={<MydataPeriodPage />} />
                <Route path="mydata/sync" element={<MydataSyncPage />} />
                <Route path="mydata/debts" element={<MydataDebtsPage />} />
                <Route
                  path="mydata/documents"
                  element={<MydataDocumentsListPage />}
                />
                <Route
                  path="mydata/pdf-export"
                  element={<MydataPdfExportPage />}
                />
                <Route
                  path="mydata/documents/:mark"
                  element={<MydataDocumentPage />}
                />
                <Route
                  path="mydata/connection"
                  element={<Navigate to="/settings/mydata-connection" replace />}
                />
              </Route>

              <Route
                element={
                  <FeatureRoute feature="reports_monthly_summary" />
                }
              >
                <Route
                  path="reports/monthly-summary"
                  element={<MonthlySummaryPage />}
                />
              </Route>
              <Route
                element={<FeatureRoute feature="reports_yearly_monthly" />}
              >
                <Route
                  path="reports/yearly-monthly"
                  element={<YearlyMonthlyPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_customer_balances" />
                }
              >
                <Route
                  path="reports/customer-balances"
                  element={<CustomerBalancesPage />}
                />
              </Route>
              <Route element={<FeatureRoute feature="reports_aging" />}>
                <Route path="reports/aging" element={<AgingPage />} />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_category_summary" />
                }
              >
                <Route
                  path="reports/category-summary"
                  element={<CategorySummaryPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_unallocated_payments" />
                }
              >
                <Route
                  path="reports/unallocated-payments"
                  element={<UnallocatedPaymentsPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_agreement_variance" />
                }
              >
                <Route
                  path="reports/agreement-variance"
                  element={<AgreementVariancePage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_monthly_summary" />
                }
              >
                <Route
                  path="reports/shipment-monthly-summary"
                  element={<ShipmentMonthlySummaryPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_expense_categories" />
                }
              >
                <Route
                  path="reports/shipment-expense-categories"
                  element={<ShipmentExpenseCategoriesPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_open_files" />
                }
              >
                <Route
                  path="reports/shipment-open-files"
                  element={<ShipmentOpenFilesPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_by_customer" />
                }
              >
                <Route
                  path="reports/shipment-by-customer"
                  element={<ShipmentByCustomerPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_container_bl" />
                }
              >
                <Route
                  path="reports/shipment-container-bl"
                  element={<ShipmentContainerBlPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_expense_analysis" />
                }
              >
                <Route
                  path="reports/shipment-expense-analysis"
                  element={<ShipmentExpenseAnalysisPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_shipment_by_port_vessel" />
                }
              >
                <Route
                  path="reports/shipment-by-port-vessel"
                  element={<ShipmentByPortVesselPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_company_expense_yearly_monthly" />
                }
              >
                <Route
                  path="reports/company-expense-yearly-monthly"
                  element={<CompanyExpenseYearlyMonthlyPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_company_expense_by_vendor" />
                }
              >
                <Route
                  path="reports/company-expense-by-vendor"
                  element={<CompanyExpenseByVendorPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_company_expense_type_analysis" />
                }
              >
                <Route
                  path="reports/company-expense-type-analysis"
                  element={<CompanyExpenseTypeAnalysisPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_company_expense_payments" />
                }
              >
                <Route
                  path="reports/company-expense-payments"
                  element={<CompanyExpensePaymentsPage />}
                />
              </Route>
              <Route
                element={
                  <FeatureRoute feature="reports_company_expense_aging" />
                }
              >
                <Route
                  path="reports/company-expense-aging"
                  element={<CompanyExpenseAgingPage />}
                />
              </Route>

              <Route element={<AdminRoute />}>
                <Route
                  element={<FeatureRoute feature="invoice_categories" />}
                >
                  <Route
                    path="settings/invoice-categories"
                    element={<InvoiceCategoriesPage />}
                  />
                </Route>
                <Route element={<FeatureRoute feature="expense_categories" />}>
                  <Route
                    path="settings/expense-categories"
                    element={<ExpenseCategoriesPage />}
                  />
                </Route>
                <Route
                  element={
                    <FeatureRoute feature="company_expense_categories" />
                  }
                >
                  <Route
                    path="settings/company-expense-categories"
                    element={<CompanyExpenseCategoriesPage />}
                  />
                </Route>
                <Route element={<FeatureRoute feature="users" />}>
                  <Route path="settings/offices" element={<OfficesPage />} />
                  <Route path="settings/users" element={<UsersPage />} />
                </Route>
                <Route element={<FeatureRoute feature="mydata" />}>
                  <Route
                    path="settings/mydata-connection"
                    element={<MydataConnectionPage />}
                  />
                </Route>
              </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ConfirmProvider>
    </AuthProvider>
    </SettingsProvider>
    </ThemeProvider>
  );
}
